import { trackEvent } from '../lib/analytics';
import React, { createContext, useContext, useEffect, useState } from 'react';

const CartContext = createContext(null);
const STORAGE_KEY = 'rl_cart';

export function CartProvider({ children }) {
  // Lazy init: read localStorage synchronously on first render so we never
  // transiently overwrite the stored cart with an empty [] before hydration.
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      // Sanitize: drop items from older app versions that are missing fields
      // we render with (price.toFixed etc.). localStorage is user-controlled
      // data from possibly months-old builds — never trust its shape.
      const clean = parsed.filter((i) =>
        i && typeof i === 'object' &&
        i.product_id && typeof i.product_id === 'string' &&
        typeof i.name === 'string' &&
        typeof i.slug === 'string' &&
        Number.isFinite(Number(i.price)) &&
        Number.isFinite(Number(i.quantity)) && Number(i.quantity) >= 1
      ).map((i) => ({
        product_id: i.product_id,
        name: i.name,
        slug: i.slug,
        price: Number(i.price),
        quantity: Math.floor(Number(i.quantity)),
        image: typeof i.image === 'string' ? i.image : '',
      }));
      // If anything was dropped, write the cleaned cart back immediately.
      if (clean.length !== parsed.length) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(clean));
      }
      return clean;
    } catch (e) {
      // Corrupted JSON — nuke it rather than crash the whole storefront.
      try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch (e) { /* storage blocked — cart works in-memory for this session */ }
  }, [items]);

  const addItem = (product, qty = 1) => {
    trackEvent('add_to_cart', { item_id: product.id, item_name: product.name, slug: product.slug, quantity: qty, value: (product.price || 0) * qty, currency: 'INR' });
    setItems((prev) => {
      const exists = prev.find((i) => i.product_id === product.id);
      if (exists) {
        return prev.map((i) => (i.product_id === product.id ? { ...i, quantity: i.quantity + qty } : i));
      }
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          slug: product.slug,
          price: product.price,
          quantity: qty,
          image: (product.images && product.images[0]) || '',
        },
      ];
    });
    // Note: we intentionally do NOT auto-open the drawer here. The sticky
    // ViewCartBar gives feedback and lets the user continue browsing —
    // matching food-delivery-app UX (Swiggy / Zomato).
  };

  const updateQty = (product_id, qty) => {
    setItems((prev) => prev.map((i) => (i.product_id === product_id ? { ...i, quantity: Math.max(1, qty) } : i)));
  };

  const remove = (product_id) => setItems((prev) => prev.filter((i) => i.product_id !== product_id));
  const clear = () => setItems([]);

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, updateQty, remove, clear, subtotal, count, open, setOpen }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
